import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { NeedsAccount } from './AccountInfo';
import { ArrowLeft, BellRing } from 'lucide-react';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { api } from '../sync/api';
import { useSyncState } from '../sync/store';
import { isIos, isStandalone } from './InstallBanner';
import { useToast } from '../ui/Toast';
import { hhmmToMinutes, minutesToHHMM } from '../ui/format';
import { updateSettings } from '../domain/actions';
import { Field, Switch } from '../ui/Field';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

type Support = 'ok' | 'unsupported' | 'ios_install';

function pushSupport(): Support {
  if (isIos() && !isStandalone()) return 'ios_install';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') return 'unsupported';
  return 'ok';
}

export function NotificationsScreen() {
  const { t } = useI18n();
  const { ctx } = useApp();
  const navigate = useNavigate();
  const toast = useToast();
  const sync = useSyncState();
  const [publicKey, setPublicKey] = useState<string | null | undefined>(undefined);
  const [permission, setPermission] = useState<NotificationPermission>(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
  const [subscribed, setSubscribed] = useState(false);
  const [explain, setExplain] = useState(false);
  const support = pushSupport();

  useEffect(() => {
    api<{ push: { publicKey: string } | null }>('/config')
      .then((c) => setPublicKey(c.push?.publicKey ?? null))
      .catch(() => setPublicKey(null));
    if (support === 'ok') {
      void navigator.serviceWorker.getRegistration().then(async (reg) => setSubscribed(Boolean(await reg?.pushManager.getSubscription())));
    }
  }, [support]);

  const enable = async () => {
    // FR-REM-03: la solicitud nativa solo tras un gesto y una explicación propia.
    const p = await Notification.requestPermission();
    setPermission(p);
    setExplain(false);
    if (p !== 'granted' || !publicKey) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource });
      await api('/push/subscriptions', { body: sub.toJSON() });
      setSubscribed(true);
      toast.show(t('notifications.enabled'));
    } catch {
      toast.show(t('sync.errors.generic'));
    }
  };

  const disable = async () => {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await api('/push/subscriptions', { method: 'DELETE', body: { endpoint: sub.endpoint } }).catch(() => {});
      await sub.unsubscribe();
    }
    setSubscribed(false);
  };

  const s = ctx.settings;

  return (
    <div className="stack-l">
      <header className="row">
        <button type="button" className="icon-btn" onClick={() => navigate('/settings')} aria-label={t('common.back')}>
          <ArrowLeft size={22} aria-hidden="true" />
        </button>
        <h1>{t('notifications.title')}</h1>
      </header>
      <p className="muted">{t('notifications.intro')}</p>

      <section className="form-section">
        {!sync.signedIn ? (
          <NeedsAccount feature="push" />
        ) : support === 'ios_install' ? (
          <p>{t('notifications.needInstallIos')}</p>
        ) : support === 'unsupported' ? (
          <p>{t('notifications.unsupported')}</p>
        ) : publicKey === null ? (
          <p>{t('notifications.notConfigured')}</p>
        ) : permission === 'denied' ? (
          <p>{t('notifications.denied')}</p>
        ) : subscribed ? (
          <>
            <p className="row">
              <BellRing size={20} aria-hidden="true" /> {t('notifications.enabled')}
            </p>
            <p className="muted small">{t('notifications.snoozeNote')}</p>
            <button
              type="button"
              className="btn btn--outline"
              onClick={async () => {
                try {
                  await api('/push/test', { body: {} });
                  toast.show(t('notifications.testSent'));
                } catch {
                  toast.show(t('sync.errors.generic'));
                }
              }}
            >
              {t('notifications.test')}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => void disable()}>
              {t('notifications.disable')}
            </button>
          </>
        ) : explain ? (
          <>
            <h2>{t('notifications.explainTitle')}</h2>
            <p className="small">{t('notifications.explainBody')}</p>
            <button type="button" className="btn btn--primary" onClick={() => void enable()}>
              {t('common.confirm')}
            </button>
          </>
        ) : (
          <button type="button" className="btn btn--primary" onClick={() => setExplain(true)} disabled={publicKey === undefined}>
            {t('notifications.enable')}
          </button>
        )}
      </section>

      <section className="form-section" aria-label={t('notifications.quietHours')}>
        <Switch
          label={t('notifications.quietHours')}
          help={t('notifications.quietHelp')}
          checked={s.quietStart !== null}
          onChange={(v) => void updateSettings(v ? { quietStart: hhmmToMinutes('22:00'), quietEnd: hhmmToMinutes('08:00') } : { quietStart: null, quietEnd: null })}
        />
        {s.quietStart !== null && s.quietEnd !== null && (
          <div className="row row--wrap">
            <div style={{ flex: '1 1 120px' }}>
              <Field label={t('notifications.from')}>
                {(p) => <input {...p} type="time" className="input num" value={minutesToHHMM(s.quietStart!)} onChange={(e) => e.target.value && void updateSettings({ quietStart: hhmmToMinutes(e.target.value) })} />}
              </Field>
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <Field label={t('notifications.to')}>
                {(p) => <input {...p} type="time" className="input num" value={minutesToHHMM(s.quietEnd!)} onChange={(e) => e.target.value && void updateSettings({ quietEnd: hhmmToMinutes(e.target.value) })} />}
              </Field>
            </div>
          </div>
        )}
      </section>

      {sync.signedIn && <MorningBrief subscribed={subscribed} />}
    </div>
  );
}

/** Resumen de la mañana por notificación: opcional, apagado por defecto, a la hora elegida. */
function MorningBrief({ subscribed }: { subscribed: boolean }) {
  const { t } = useI18n();
  const toast = useToast();
  const [state, setState] = useState<{ enabled: boolean; time: string } | null>(null);
  useEffect(() => {
    let alive = true;
    api<{ enabled: boolean; time: string }>('/push/brief')
      .then((b) => alive && setState(b))
      .catch(() => alive && setState(null));
    return () => {
      alive = false;
    };
  }, []);
  if (!state) return null;
  const save = async (next: { enabled: boolean; time: string }) => {
    const prev = state;
    setState(next);
    try {
      await api('/push/brief', { method: 'PUT', body: next });
    } catch {
      setState(prev);
      toast.show(t('notifications.briefFailed'));
    }
  };
  return (
    <section className="form-section" aria-label={t('notifications.brief')}>
      <Switch label={t('notifications.brief')} help={t('notifications.briefHelp')} checked={state.enabled} onChange={(v) => void save({ ...state, enabled: v })} />
      {state.enabled && (
        <Field label={t('notifications.briefTime')}>
          {(p) => <input {...p} type="time" className="input num" value={state.time} onChange={(e) => e.target.value && void save({ ...state, time: e.target.value })} />}
        </Field>
      )}
      {state.enabled && !subscribed && <p className="small">{t('notifications.briefNeedsPush')}</p>}
    </section>
  );
}
